import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  ChangeEvent,
  FormEvent,
  memo
} from 'react';
import { AxiosError } from 'axios';

import testIds, { loginEmailInputFieldFocusKey } from '@smart-tv/e2e/shared/variables';

import Button from '@smart-tv/components/button/button';
import Form from '@smart-tv/components/form/form';
import PasswordField from '@smart-tv/components/password-field/password-field';
import Input from '@smart-tv/components/input';
import Typography from '@smart-tv/components/typography';
import { CmdError } from '@smart-tv/contexts/error-context/error-context';
import { KMMError } from '@norlys-a-s/norlys-play-kmm';

import useAuthentication from '@smart-tv/hooks/use-authentication/use-authentication';

import { SentryClient } from '@smart-tv/sentry/SentryClient';
import { ERROR_MESSAGES } from '@smart-tv/pages/login/constants';

import isEmail from '@smart-tv/pages/login/is-email';
import {
  storePersistentData,
  retrievePersistentData
} from '@smart-tv/utils/persistent-data/persistent-data';

import LoginContext from '../login-context';

import styles from '../login.module.scss';

import LoginError from './login-error';
import { useSetAtom } from 'jotai';
import { headerActiveAtom } from '@smart-tv/atoms';
import { links } from '@smart-tv/components/header/header-navigation';
import { HEADER_HOME } from '@smart-tv/components/header/header-helpers';
import { checkForAutoTest } from '@smart-tv/utils/auto-test-handling';

// Derived from links instead of hardcoded, since its position can change (e.g. Søg).
const HOME_INDEX = links.findIndex((link) => link.focusKey === HEADER_HOME);

interface Props {
  handleOpenForgotPassword: () => void;
  handleOpenGDPR: () => void;
}

export const LoginContent = (props: Props) => {
  const { handleOpenForgotPassword, handleOpenGDPR } = props;
  const { email, setEmail } = useContext(LoginContext);

  const lastLoginEmail = retrievePersistentData<string>('last-login-email');
  const setActiveHeader = useSetAtom(headerActiveAtom);

  const [loginForm, setLoginForm] = useState({
    email: email || lastLoginEmail || '',
    password: ''
  });

  const [loginDisabled, setLoginDisabled] = useState(true);

  const { sendCredentials } = useAuthentication();

  const [loginErrorMessage, setLoginErrorMessage] = useState<string>();
  const [loginErrorCode, setLoginErrorCode] = useState<number | undefined>();

  const handleErrorCodes = useCallback((error: unknown) => {
    const axiosError = error as AxiosError;
    const kmmError = error as KMMError.ApiError;
    const cmdError = error as CmdError;

    if (axiosError.code === AxiosError.ERR_NETWORK) {
      setLoginErrorMessage(ERROR_MESSAGES.NO_NETWORK);
      setLoginErrorCode(undefined);
    } else if (error instanceof KMMError.ApiError) {
      setLoginErrorMessage(kmmError._message);
      setLoginErrorCode(parseInt(kmmError.code));
    } else {
      setLoginErrorMessage(cmdError.info);
      setLoginErrorCode(cmdError.code);
    }
  }, []);

  const handleInputChange = useCallback(
    (event: ChangeEvent<any>) => {
      setLoginForm((prevValue) => ({ ...prevValue, [event.target.name]: event.target.value }));
      if (event.target.name === 'email') {
        setEmail(event.target.value);
      }
    },
    [setEmail]
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const { email, password } = loginForm;
      try {
        await sendCredentials(email, password);

        checkForAutoTest(email);

        storePersistentData('last-login-email', email);
        const customerId = retrievePersistentData<string>('customerId');
        if (customerId) {
          SentryClient.setUser({ id: customerId, email: email });
        }

        setActiveHeader(HOME_INDEX); // Reset header active state to home
      } catch (error) {
        handleErrorCodes(error);
      }
    },
    [loginForm, sendCredentials, handleErrorCodes, setActiveHeader]
  );

  useEffect(() => {
    const emailValid = isEmail(loginForm.email);
    const passwordValid = loginForm.password.length > 1;
    setLoginDisabled(!(emailValid && passwordValid));
  }, [loginForm.email, loginForm.password]);

  return (
    <div className={styles['login-form']}>
      <Typography
        className={styles['login-form__title']}
        variant="h3"
        data-testid={testIds.login.header}
      >
        Log ind for at komme i gang med at se dine kanaler
      </Typography>
      <Form className={styles['login-form__form']} onSubmit={handleSubmit}>
        <Input
          focusKey={loginEmailInputFieldFocusKey}
          className={styles['login-form__fieldset']}
          type="email"
          name="email"
          value={loginForm.email}
          onChange={handleInputChange}
          placeholder="Indtast e-mail"
          inputProps={{ 'data-testid': testIds.login.email }}
        />
        <PasswordField
          className={styles['login-form__fieldset']}
          name="password"
          placeholder="Adgangskode"
          value={loginForm.password}
          onChange={handleInputChange}
          inputProps={{ 'data-testid': testIds.login.password }}
        />
        <LoginError
          errorMsg={loginErrorMessage}
          errorCode={loginErrorCode}
          data-testid={testIds.login.errorMessage}
        />
        <Button
          className={styles['login-form__accept-button']}
          type="submit"
          disabled={loginDisabled}
          data-testid={testIds.login.submit}
        >
          Log ind
        </Button>
        <Button onClick={handleOpenForgotPassword} data-testid={testIds.login.forgotPassword}>
          Glemt adgangskode
        </Button>
      </Form>
      <div className={styles['login-form__gdpr']}>
        <Typography
          className={styles['login-form__gdpr-disclaimer']}
          variant="caption"
          data-testid={testIds.login.gdprText}
        >
          Ved login indsamles brugerdata og personoplysninger.
        </Typography>
        <Button
          className={styles['login-form__read-more-button']}
          size="sm"
          onClick={handleOpenGDPR}
          data-testid={testIds.login.readGdpr}
        >
          Læs mere her
        </Button>
      </div>
    </div>
  );
};

export default memo(LoginContent);
